import { MediaPreview } from "@/components/studio/file-upload";
import { UploadDropzone } from "@/components/studio/upload-dropzone";
import { UploadProgress } from "@/components/studio/upload-progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import type { Coach } from "@/lib/schema";
import {
  THUMBNAIL_ACCEPT_ATTR,
  THUMBNAIL_HINT,
  VIDEO_ACCEPT_ATTR,
  videoHint,
} from "@/lib/thumbnail-validation";
import { RectangleHorizontal, RectangleVertical } from "lucide-react";

interface VariantData {
  languageCode: string;
  isDefault: boolean;
  localizedTitle: string;
  localizedDescription: string;
  videoFileUrl: string | null;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  coachId: string | null;
  coachName?: string | null;
  transcript: string | null;
  videoRatio: "LANDSCAPE" | "PORTRAIT" | null;
}

export interface VideoVariantErrors {
  localizedTitle?: string;
  videoRatio?: string;
  videoFileUrl?: string;
  thumbnailUrl?: string;
}

interface VideoVariantFieldsProps {
  variant: VariantData;
  onFieldChange: (field: keyof VariantData, value: any) => void;
  onFileUpload: (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "videoFileUrl" | "thumbnailUrl",
  ) => void;
  coaches: Coach[];
  isUploading: boolean;
  uploadingField?: string | null;
  uploadProgress?: number;
  uploadFileName?: string | null;
  onCancelUpload?: () => void;
  errors?: VideoVariantErrors;
  isReadOnly?: boolean;
  isCoachLocked?: boolean;
}

export function VideoVariantFields({
  variant,
  onFieldChange,
  onFileUpload,
  coaches,
  isUploading,
  uploadingField,
  uploadProgress,
  uploadFileName,
  onCancelUpload,
  errors,
  isReadOnly = false,
  isCoachLocked = false,
}: VideoVariantFieldsProps) {
  const lockedCoachName =
    isCoachLocked && variant.coachId
      ? (variant.coachName ??
        coaches.find((c) => c.id === variant.coachId)?.name ??
        "")
      : "";
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Title *</Label>
        <Input
          value={variant.localizedTitle}
          onChange={(e) => onFieldChange("localizedTitle", e.target.value)}
          placeholder="Enter video title"
          disabled={isReadOnly}
          data-testid="input-variant-title"
          className={errors?.localizedTitle ? "border-destructive" : ""}
        />
        {errors?.localizedTitle && (
          <p className="text-sm text-destructive">{errors.localizedTitle}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea
          value={variant.localizedDescription}
          onChange={(e) =>
            onFieldChange("localizedDescription", e.target.value)
          }
          placeholder="Enter video description"
          rows={3}
          maxLength={150}
          disabled={isReadOnly}
          data-testid="input-variant-description"
        />
        <p className="text-xs text-muted-foreground text-right">
          {(variant.localizedDescription?.length ?? 0)}/150
        </p>
      </div>

      <div className="space-y-2">
        <Label>Video Orientation *</Label>
        <Select
          value={variant.videoRatio || ""}
          onValueChange={(val) =>
            onFieldChange("videoRatio", val as "LANDSCAPE" | "PORTRAIT")
          }
          disabled={isReadOnly}
        >
          <SelectTrigger
            data-testid="select-variant-orientation"
            className={errors?.videoRatio ? "border-destructive" : ""}
          >
            <SelectValue placeholder="Select orientation" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="LANDSCAPE">
              <span className="inline-flex items-center gap-2">
                <RectangleHorizontal className="h-4 w-4" />
                Landscape
              </span>
            </SelectItem>
            <SelectItem value="PORTRAIT">
              <span className="inline-flex items-center gap-2">
                <RectangleVertical className="h-4 w-4" />
                Portrait
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        {errors?.videoRatio && (
          <p className="text-sm text-destructive">{errors.videoRatio}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Video File *</Label>
        {variant.videoFileUrl ? (
          <MediaPreview
            url={variant.videoFileUrl}
            mediaType="video"
            onRemove={
              isReadOnly ? undefined : () => onFieldChange("videoFileUrl", null)
            }
            onReplace={
              isReadOnly ? undefined : (e) => onFileUpload(e, "videoFileUrl")
            }
            replaceAccept={VIDEO_ACCEPT_ATTR}
            isReadOnly={isReadOnly}
          />
        ) : (
          <UploadDropzone
            type="video"
            label="Upload Video"
            hint={videoHint(variant.videoRatio)}
            accept={VIDEO_ACCEPT_ATTR}
            onChange={(e) => onFileUpload(e, "videoFileUrl")}
            isUploading={isUploading && uploadingField === "videoFileUrl"}
            disabled={
              isReadOnly || (isUploading && uploadingField !== "videoFileUrl")
            }
            testId="button-upload-video"
          />
        )}
        {uploadingField === "videoFileUrl" && (
          <UploadProgress
            progress={uploadProgress ?? 0}
            isUploading={isUploading}
            fileName={uploadFileName ?? undefined}
            onCancel={onCancelUpload}
          />
        )}
        {errors?.videoFileUrl && (
          <p className="text-sm text-destructive">{errors.videoFileUrl}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Thumbnail *</Label>
        {variant.thumbnailUrl ? (
          <MediaPreview
            url={variant.thumbnailUrl}
            mediaType="image"
            onRemove={
              isReadOnly ? undefined : () => onFieldChange("thumbnailUrl", null)
            }
            onReplace={
              isReadOnly ? undefined : (e) => onFileUpload(e, "thumbnailUrl")
            }
            replaceAccept={THUMBNAIL_ACCEPT_ATTR}
            isReadOnly={isReadOnly}
          />
        ) : (
          <UploadDropzone
            type="image"
            label="Upload Thumbnail"
            hint={THUMBNAIL_HINT}
            accept={THUMBNAIL_ACCEPT_ATTR}
            onChange={(e) => onFileUpload(e, "thumbnailUrl")}
            isUploading={isUploading && uploadingField === "thumbnailUrl"}
            disabled={
              isReadOnly || (isUploading && uploadingField !== "thumbnailUrl")
            }
            testId="button-upload-thumbnail"
          />
        )}
        {errors?.thumbnailUrl && (
          <p className="text-sm text-destructive">{errors.thumbnailUrl}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Duration (seconds)</Label>
          <Input
            type="number"
            value={variant.durationSeconds ?? ""}
            onChange={(e) =>
              onFieldChange(
                "durationSeconds",
                e.target.value ? Number(e.target.value) : null,
              )
            }
            placeholder="Auto-detected on upload"
            min={0}
            disabled={isReadOnly}
            data-testid="input-variant-duration"
          />
        </div>

        <div className="space-y-2">
          <Label>Coach Attribution</Label>
          {isCoachLocked ? (
            <Input
              value={lockedCoachName}
              disabled
              data-testid="select-variant-coach"
            />
          ) : (
            <Select
              value={variant.coachId || "none"}
              onValueChange={(val) =>
                onFieldChange("coachId", val === "none" ? null : val)
              }
              disabled={isReadOnly}
            >
              <SelectTrigger data-testid="select-variant-coach">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {coaches.map((coach) => (
                  <SelectItem key={coach.id} value={coach.id}>
                    {coach.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label>Transcript (optional)</Label>
        <Textarea
          value={variant.transcript ?? ""}
          onChange={(e) => onFieldChange("transcript", e.target.value || null)}
          placeholder="Enter video transcript..."
          rows={4}
          disabled={isReadOnly}
          data-testid="input-variant-transcript"
        />
      </div>
    </div>
  );
}
